import React, { useEffect, useState, useMemo } from "react";
import { PieChart } from "@mui/x-charts/PieChart";
import { getTreatmentPieChart, TreatmentPieChartResponse } from "../../../api/dashboardService";
import PieChartIcon from "@mui/icons-material/PieChart";

type TreatmentChartItem = TreatmentPieChartResponse["data"][number];

const TreatmentPieChart = React.memo(() => {
  const [chart, setchart] = useState<TreatmentChartItem[]>([]);
  const [chartdata, setchartdata] = useState<{
    c1: TreatmentChartItem[];
    c2: TreatmentChartItem[];
  }>({
    c1: [],
    c2: [],
  });
  const [select, setselect] = useState("month");

  useEffect(() => {
    getTreatmentPieChart()
      .then((data) => {
        setchart(data.data);
        setchartdata({
          c1: data.data,
          c2: data.treatmenttoday,
        });
      })
      .catch((error) => {
        console.error(error.response);
      });
  }, []);

  const handleChange = (value: string) => {
    setselect(value);
    if (value === "month") {
      setchart(chartdata.c1);
    } else {
      setchart(chartdata.c2);
    }
  };

  // Calculate total and percentages
  const { total, chartWithPercentage } = useMemo(() => {
    const total = chart.reduce((sum, item) => sum + (item.value || 0), 0);
    const chartWithPercentage = chart.map((item) => ({
      ...item,
      percentage: total > 0 ? ((item.value / total) * 100).toFixed(1) : 0,
    }));
    return { total, chartWithPercentage };
  }, [chart]);

  return (
    <div className="rounded-2xl shadow-lg border overflow-hidden h-full transition-all duration-300 bg-white border-slate-100 shadow-slate-200/50 dark:bg-slate-800 dark:border-slate-700 dark:shadow-slate-900/50">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
            <PieChartIcon className="text-white" sx={{ fontSize: 20 }} />
          </div>
          <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">Treatment Distribution</h3>
        </div>
        <div className="flex gap-1 p-1 rounded-lg bg-slate-100 dark:bg-slate-700">
          <button
            onClick={() => handleChange("month")}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
              select === "month"
                ? "bg-white text-emerald-600 shadow-sm dark:bg-slate-600 dark:text-emerald-300"
                : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
            }`}
          >
            Month
          </button>
          <button
            onClick={() => handleChange("week")}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
              select === "week"
                ? "bg-white text-emerald-600 shadow-sm dark:bg-slate-600 dark:text-emerald-300"
                : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
            }`}
          >
            Week
          </button>
        </div>
      </div>

      {/* Chart & Details */}
      <div className="p-4">
        {chart?.length === 0 ? (
          <div className="py-16 text-center text-slate-400 dark:text-slate-500">
            <PieChartIcon sx={{ fontSize: 48 }} className="mb-2" />
            <p>No treatment data available</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Pie Chart */}
            <div className="flex justify-center">
              <PieChart
                series={[
                  {
                    data: chart,
                    innerRadius: 40,
                    outerRadius: 80,
                    paddingAngle: 2,
                    cornerRadius: 4,
                  },
                ]}
                width={250}
                height={200}
                slotProps={{
                  legend: { hidden: true },
                }}
              />
            </div>

            {/* Total */}
            <div className="text-center py-2 border-b border-slate-200 dark:border-slate-700">
              <span className="text-sm text-slate-500 dark:text-slate-400">Total Treatments: </span>
              <span className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{total}</span>
            </div>

            {/* Details Grid - 4 columns, scrollable */}
            <div className="max-h-[300px] overflow-y-auto pr-1">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {chartWithPercentage.map((item, index) => (
                  <div
                    key={index}
                    className="p-2 rounded-lg transition-colors text-center bg-slate-50 hover:bg-slate-100 dark:bg-slate-700/50 dark:hover:bg-slate-700"
                  >
                    <div className="flex items-center justify-center gap-1.5 mb-1">
                      <div
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: item.color || `hsl(${index * 25}, 70%, 50%)` }}
                      />
                      <span className="text-xs font-medium truncate text-slate-700 dark:text-slate-300">
                        {item.label}
                      </span>
                    </div>
                    <div className="flex items-center justify-center gap-1">
                      <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">{item.value}</span>
                      <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border dark:border-emerald-800/50">
                        {item.percentage}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

export default TreatmentPieChart;
